import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import Table from '../Table.js';
import { warn } from '../../utils/logger.js';
const AlarmsTable = ({ alarms = [], alarmSettings = [], sources = [] }) => {
  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    if (!Array.isArray(alarms)) {
      setTableData([]);
      return;
    }

    const data = alarms.map((alarm) => {
      const requiredDataSource = sources.find((source) => source.id === alarm.sourceId);
      const requiredAlarmSetting = alarmSettings.find(
        (setting) => setting.id === alarm.alarmSettingId
      );
      return {
        createdAt: alarm.createdAt,
        sourceName: requiredDataSource ? requiredDataSource.name : '',
        value: alarm.value || 'N/A', // Fallback to 'N/A' if value is missing
        title: requiredAlarmSetting ? requiredAlarmSetting.title : '',
        condition: requiredAlarmSetting
          ? `${requiredAlarmSetting.direction} ${requiredAlarmSetting.value}`
          : '',
        color: requiredAlarmSetting ? requiredAlarmSetting.color : '#FFFFFF',
      };
    });
    setTableData(data);
  }, [alarms, alarmSettings, sources]);

  const columns = [
    { title: 'color', field: 'color', headerSort: false, visible: false },
    { title: 'Timestamp', field: 'createdAt' },
    { title: 'Tag', field: 'sourceName', headerFilter: true },
    { title: 'Value', field: 'value' },
    { title: 'Title', field: 'title', headerFilter: true },
    { title: 'Trigger Condition', field: 'condition' },
  ];

  // Improved function to check if the background color is dark
  const isDarkColor = (color) => {
    if (!color) return false;
    
    let r, g, b;
    
    // Handle hex color format (e.g., "#000000" or "#000")
    if (color.startsWith('#')) {
      let hex = color.replace('#', '');
      
      // Handle 3-digit hex colors
      if (hex.length === 3) {
        hex = hex.split('').map(char => char + char).join('');
      }
      
      // Ensure we have a valid 6-digit hex
      if (hex.length === 6) {
        r = parseInt(hex.substr(0, 2), 16);
        g = parseInt(hex.substr(2, 2), 16);
        b = parseInt(hex.substr(4, 2), 16);
      } else {
        return false; // Invalid hex format
      }
    } 
    // Handle rgb/rgba format (e.g., "rgb(0, 0, 0)" or "rgba(0, 0, 0, 1)")
    else if (color.includes('rgb')) {
      const rgb = color.match(/\d+/g);
      if (rgb && rgb.length >= 3) {
        [r, g, b] = rgb.slice(0, 3).map(Number);
      } else {
        return false; // Invalid rgb format
      }
    }
    // Handle named colors (basic support)
    else {
      const namedColors = {
        'black': [0, 0, 0],
        'darkblue': [0, 0, 139],
        'darkgreen': [0, 100, 0],
        'darkred': [139, 0, 0],
        'navy': [0, 0, 128],
        'maroon': [128, 0, 0],
        'purple': [128, 0, 128],
        'darkmagenta': [139, 0, 139],
        'darkcyan': [0, 139, 139],
        'darkgray': [169, 169, 169],
        'darkgrey': [169, 169, 169]
      };
      
      const colorLower = color.toLowerCase();
      if (namedColors[colorLower]) {
        [r, g, b] = namedColors[colorLower];
      } else {
        return false; // Unknown color format, default to light
      }
    }
    
    // Calculate brightness using the relative luminance formula
    // This is more accurate than the simple weighted average
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5; // Threshold for "dark" color
  };

  // Helper function to adjust color brightness
  const adjustColorBrightness = (color, amount) => {
    if (!color) return color;
    
    let r, g, b;
    
    // Handle hex color format
    if (color.startsWith('#')) {
      let hex = color.replace('#', '');
      if (hex.length === 3) {
        hex = hex.split('').map(char => char + char).join('');
      }
      if (hex.length === 6) {
        r = parseInt(hex.substr(0, 2), 16);
        g = parseInt(hex.substr(2, 2), 16);
        b = parseInt(hex.substr(4, 2), 16);
      } else {
        return color; // Invalid hex format
      }
    } 
    // Handle rgb format
    else if (color.includes('rgb')) {
      const rgb = color.match(/\d+/g);
      if (rgb && rgb.length >= 3) {
        [r, g, b] = rgb.slice(0, 3).map(Number);
      } else {
        return color; // Invalid rgb format
      }
    } else {
      return color; // Unsupported format for adjustment
    }
    
    // Adjust brightness
    r = Math.max(0, Math.min(255, r + amount));
    g = Math.max(0, Math.min(255, g + amount));
    b = Math.max(0, Math.min(255, b + amount));
    
    return `rgb(${r}, ${g}, ${b})`;
  };

  const rowFormatter = (row) => {
    const bgColor = row.getData().color;
    const element = row.getElement();
    const isDark = isDarkColor(bgColor);
    const textColor = isDark ? 'white' : 'black';
    
    // Create hover color - lighten dark colors, darken light colors
    const hoverColor = isDark 
      ? adjustColorBrightness(bgColor, 40)  // Lighten dark colors
      : adjustColorBrightness(bgColor, -30); // Darken light colors
    
    // Set background color
    element.style.backgroundColor = bgColor;
    
    // Create a unique class name for this specific row
    const uniqueClass = `alarm-row-${Math.random().toString(36).substr(2, 9)}`;
    element.classList.add(uniqueClass);
    
    // Add custom CSS specifically for this row that handles all states
    if (!document.getElementById('alarms-table-dynamic-styles')) {
      const style = document.createElement('style');
      style.id = 'alarms-table-dynamic-styles';
      document.head.appendChild(style);
    }
    
    const styleSheet = document.getElementById('alarms-table-dynamic-styles').sheet;
    
    // Add rules for this specific row
    const normalRule = `.${uniqueClass}, .${uniqueClass} .tabulator-cell, .${uniqueClass} .tabulator-cell * { 
      color: ${textColor} !important; 
      font-weight: ${isDark ? '500' : 'normal'}; 
      transition: all 0.2s ease-in-out;
    }`;
    
    const hoverRule = `.${uniqueClass}:hover, .${uniqueClass}:hover .tabulator-cell { 
      background-color: ${hoverColor} !important; 
      color: ${textColor} !important;
      transform: scale(1.01);
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      cursor: pointer;
    }`;
    
    const hoverTextRule = `.${uniqueClass}:hover .tabulator-cell, .${uniqueClass}:hover .tabulator-cell * { 
      color: ${textColor} !important; 
    }`;
    
    try {
      styleSheet.insertRule(normalRule, styleSheet.cssRules.length);
      styleSheet.insertRule(hoverRule, styleSheet.cssRules.length);
      styleSheet.insertRule(hoverTextRule, styleSheet.cssRules.length);
    } catch (error) {
      // Fallback if CSS rule insertion fails
      warn('Could not insert CSS rule:', error);
    }
  };

  // Clean up dynamic styles when component unmounts
  useEffect(() => {
    return () => {
      const dynamicStyles = document.getElementById('alarms-table-dynamic-styles');
      if (dynamicStyles) {
        dynamicStyles.remove();
      }
    };
  }, []);

  return (
    <div className="d-flex justify-content-center">
      <div style={{ width: '60rem' }}>
        <Table
          tableID="alarmsTable"
          tableData={tableData}
          tableColumns={columns}
          rowFormatter={rowFormatter}
          isSelectable={false}
        />
      </div>
    </div>
  );
};

export default AlarmsTable;